// Wire protocol v1 — see docs/protocol.md. Keep in lockstep with
// gateway/src/Gateway.Core/Protocol/Messages.cs (use the /protocol-change command).

/** Bump on breaking changes, together with ProtocolInfo.ProtocolVersion in Messages.cs. */
export const PROTOCOL_VERSION = 1;

/** Metres, region-local, Z-up (OpenSim native). The engine converts; nothing else does. */
export type Vec3 = [x: number, y: number, z: number];
/** Unit quaternion, Z-up frame. */
export type Quat = [x: number, y: number, z: number, w: number];
/** Colour components 0..1, sRGB as OpenSim stores them. */
export type Rgba = [r: number, g: number, b: number, a: number];

/** The built-in dance animations every OpenSim / SL viewer has (see "Dances" in docs/protocol.md). */
export const DANCES = [
  'dance1',
  'dance2',
  'dance3',
  'dance4',
  'dance5',
  'dance6',
  'dance7',
  'dance8',
] as const;
export type DanceId = (typeof DANCES)[number];

// ───────────────────────── Viewer → Gateway ─────────────────────────

export interface HelloMessage {
  type: 'hello';
  protocolVersion: number;
  client: string;
  /** An event id from the page's link (see "Events" in docs/protocol.md). */
  event?: string;
}
export interface LoginMessage {
  type: 'login';
  firstName: string;
  lastName: string;
  password: string;
  startRegion?: string;
  /** An event the gateway confirmed in `welcome`: where to arrive. */
  event?: string;
}
export interface ChatSendMessage {
  type: 'chat';
  text: string;
  channel?: number;
}
export type Axis = -1 | 0 | 1;
export interface MoveMessage {
  type: 'move';
  forward: Axis;
  turn: Axis;
  run?: boolean;
}
export interface PingMessage {
  type: 'ping';
  t: number;
}
export interface LogoutMessage {
  type: 'logout';
}
/** Start a dance, replacing any current one; null stops. */
export interface DanceMessage {
  type: 'dance';
  dance: DanceId | null;
}

export type ClientMessage =
  HelloMessage | LoginMessage | ChatSendMessage | MoveMessage | PingMessage | LogoutMessage | DanceMessage;

// ───────────────────────── Gateway → Viewer ─────────────────────────

/** An event as the gateway describes it: what it's called and where. */
export interface EventInfo {
  id: string;
  title: string;
  region: string;
}
export interface WelcomeMessage {
  type: 'welcome';
  protocolVersion: number;
  gatewayVersion: string;
  gridName: string;
  mode: 'demo' | 'opensim';
  /** Only when `hello.event` named an event the gateway knows. */
  event?: EventInfo;
}

/** 1–40 of a–z, 0–9 and '-', starting with a letter or digit (docs/protocol.md, "Events"). */
const EVENT_ID = /^[a-z0-9][a-z0-9-]{0,39}$/;

/**
 * The event a page link asks for (`?event=friday-house`), or null if there's none or it
 * isn't a valid id. Lowercased, so a hand-typed `Friday-House` still works. The gateway
 * rejects invalid ids in `hello`, which would close the connection, so they're never sent.
 */
export function eventFromSearch(search: string): string | null {
  const id = new URLSearchParams(search).get('event')?.trim().toLowerCase();
  return id && EVENT_ID.test(id) ? id : null;
}
export interface LoginResultMessage {
  type: 'loginResult';
  ok: boolean;
  agentId?: string;
  displayName?: string;
  message?: string;
}
export interface RegionMessage {
  type: 'region';
  name: string;
  sizeX: number;
  sizeY: number;
  waterHeight: number;
}
/**
 * The region's heightmap (see "Terrain" in docs/protocol.md). `data` is base64 of
 * samplesX·samplesY little-endian uint16, Y outermost; decode with `decodeTerrain`.
 */
export interface TerrainMessage {
  type: 'terrain';
  samplesX: number;
  samplesY: number;
  spacing: number;
  minHeight: number;
  maxHeight: number;
  data: string;
}
export interface AvatarAddedMessage {
  type: 'avatarAdded';
  id: string;
  name: string;
  position: Vec3;
  rotation: Quat;
  self: boolean;
  /** Set if the avatar is already dancing when it appears. */
  dance?: DanceId;
}
export interface AvatarPose {
  id: string;
  position: Vec3;
  rotation: Quat;
}
export interface AvatarMovedMessage {
  type: 'avatarMoved';
  updates: AvatarPose[];
}
export interface AvatarRemovedMessage {
  type: 'avatarRemoved';
  id: string;
}
/** An avatar started or switched a dance, or stopped (null). Includes your own. */
export interface AvatarDanceMessage {
  type: 'avatarDance';
  id: string;
  dance: DanceId | null;
}
/**
 * Prim shapes (see "Objects" in docs/protocol.md). The list may grow without a version bump:
 * draw a shape you don't know as a box (`primShapeNumber`).
 */
export const PRIM_SHAPES = ['box', 'cylinder', 'sphere'] as const;
export type PrimShape = (typeof PRIM_SHAPES)[number];
/** One prim's whole state, region frame (the gateway resolves linksets). */
export interface Prim {
  id: string;
  /** One of PRIM_SHAPES today; a newer gateway may send others (draw them as boxes). */
  shape: PrimShape | (string & Record<never, never>);
  position: Vec3;
  rotation: Quat;
  /** Size in metres along the prim's own X, Y, Z. */
  scale: Vec3;
  /** Default face colour; a = 0 means hidden, 0 < a < 1 see-through. */
  color: Rgba;
  /** Lit by itself, ignoring the scene's light. Absent = false. */
  fullbright?: boolean;
  /** Glow 0..1. Absent = 0. */
  glow?: number;
  /** Hollow 0..0.95 of the prim's size. Absent = 0 (solid). */
  hollow?: number;
  /** [begin, end], the part of the profile kept, 0..1 around it, end − begin ≥ 0.02. Absent = [0, 1]. */
  profileCut?: [number, number];
}
export interface ObjectsUpdatedMessage {
  type: 'objectsUpdated';
  objects: Prim[];
}
export interface ObjectsRemovedMessage {
  type: 'objectsRemoved';
  ids: string[];
}

/** The engine's number for a shape: its index in PRIM_SHAPES, or 0 (box) for one we don't know. */
export function primShapeNumber(shape: string): number {
  return Math.max(0, (PRIM_SHAPES as readonly string[]).indexOf(shape));
}

export type ChatSourceType = 'agent' | 'object' | 'system';
export interface ChatMessage {
  type: 'chat';
  fromId: string;
  fromName: string;
  /** Untrusted: render with textContent only. */
  text: string;
  sourceType: ChatSourceType;
}
export interface MusicMessage {
  type: 'music';
  url: string | null;
}
export interface PongMessage {
  type: 'pong';
  t: number;
}
export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

export type ServerMessage =
  | WelcomeMessage
  | LoginResultMessage
  | RegionMessage
  | TerrainMessage
  | AvatarAddedMessage
  | AvatarMovedMessage
  | AvatarRemovedMessage
  | AvatarDanceMessage
  | ObjectsUpdatedMessage
  | ObjectsRemovedMessage
  | ChatMessage
  | MusicMessage
  | PongMessage
  | ErrorMessage;

const SERVER_TYPES: ReadonlySet<string> = new Set<ServerMessage['type']>([
  'welcome',
  'loginResult',
  'region',
  'terrain',
  'avatarAdded',
  'avatarMoved',
  'avatarRemoved',
  'avatarDance',
  'objectsUpdated',
  'objectsRemoved',
  'chat',
  'music',
  'pong',
  'error',
]);

/** Most samples per side in a `terrain` message (docs/protocol.md, "Terrain"). */
export const TERRAIN_MAX_SAMPLES = 1024;

/**
 * Heights in metres from a `terrain` message, Y outermost, or null if the message doesn't add up:
 * sizes outside 2…TERRAIN_MAX_SAMPLES, a bad spacing or height range, or `data` not holding
 * exactly samplesX × samplesY values.
 */
export function decodeTerrain(message: TerrainMessage): Float32Array | null {
  const { samplesX, samplesY, spacing, minHeight, maxHeight, data } = message;
  const side = (n: number) => Number.isInteger(n) && n >= 2 && n <= TERRAIN_MAX_SAMPLES;
  if (!side(samplesX) || !side(samplesY)) return null;
  if (!(spacing > 0) || !Number.isFinite(spacing)) return null;
  if (!Number.isFinite(minHeight) || !Number.isFinite(maxHeight) || maxHeight < minHeight) return null;
  const count = samplesX * samplesY;
  // Check the length before decoding anything: base64 is 4 characters per 3 bytes, padded.
  if (typeof data !== 'string' || data.length !== 4 * Math.ceil((count * 2) / 3)) return null;
  let binary: string;
  try {
    binary = atob(message.data);
  } catch {
    return null;
  }
  if (binary.length !== count * 2) return null;
  const range = maxHeight - minHeight;
  const heights = new Float32Array(count);
  for (let k = 0; k < count; k++) {
    // Little-endian uint16, read byte by byte so the platform's endianness doesn't matter.
    const v = binary.charCodeAt(2 * k) | (binary.charCodeAt(2 * k + 1) << 8);
    heights[k] = minHeight + (v / 65535) * range;
  }
  return heights;
}

/** WebSocket close codes the gateway uses. */
export const CloseCodes = {
  HelloExpected: 4000,
  VersionMismatch: 4001,
  BadFrame: 4002,
  TooSlow: 4003,
  GridDisconnected: 4004,
} as const;

/**
 * Parses a gateway frame. The gateway is ours, so this checks the envelope rather
 * than every field; unknown types return null (forward compatibility).
 */
export function parseServerMessage(text: string): ServerMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== 'string' || !SERVER_TYPES.has(type)) return null;
  return value as ServerMessage;
}
